import api from 'api/apiClient';
import { ensureFileId } from 'modules/shared/service/fileUpload.service';
import { formatDate } from 'modules/shared/utils';
import { WORK_AGE_MAP, WORK_CATEGORY_MAP } from 'modules/work-implementation/constants';
import {
  calculateProjectTimeLeft,
  formatTimeLeft,
} from 'modules/work-implementation/projects-list/utils';

const unwrap = async (promise) => {
  const res = await promise;
  return res.data;
};

export async function getEmployeeProjectView(employeeId, jobId) {
  if (!employeeId || !jobId) throw new Error('Employee id and job id are required');

  const { data: raw } = await unwrap(await api.get(`/api/employee/${employeeId}/job/${jobId}`));
  // The API answers `status: false` with no data when none of the job's tasks are this user's.
  if (!raw) return null;

  const tasks = (raw.tasks ?? []).map((task) => ({
    id: task.id,
    name: task.name,
    totalTime: task.alloted_time,
    timeUsed: task.time_used,
    lastPlay: task.last_play,
    lastPause: task.last_pause,
    documentsShared: task.documents_shared,
    note: task.note,
    status: task.status,
    currentWorkingStatus: task.status,
    submissions: (task.submissions ?? []).map((sub) => ({
      id: sub.id,
      documentUrl: sub.file_url,
      documentName: sub.file_name,
      comment: sub.employee_note,
      revisionNumber: sub.revision_number,
      status: sub.revision_request_reason ? 'REVISION_REQUESTED' : 'SUBMITTED',
      revisionReason: sub.revision_request_reason,
    })),
  }));
  const timeLeft = calculateProjectTimeLeft(tasks, new Date());

  return {
    id: raw.job_id,
    projectDetails: {
      id: raw.job_id,
      name: raw.job_name,
      code: raw.job_code,
      status: raw.job_status,
      date: formatDate(raw.job_date),
      timeLeft: formatTimeLeft(timeLeft),
      siteAddress: raw.site_address,
      locality: raw.locality,
      typeOfWork: {
        workAge: WORK_AGE_MAP[raw.type_of_work?.job_type],
        workCategory: WORK_CATEGORY_MAP[raw.type_of_work?.job_work_type],
        alliedWorkDetails: raw.type_of_work?.allied_work_details,
        buildingType: raw.type_of_work?.building_type,
        basementCount: raw.type_of_work?.basement_count,
        parkingCount: raw.type_of_work?.parking_count,
        groundFloor: raw.type_of_work?.ground_floor,
        upperFloorCount: raw.type_of_work?.upper_floor_count,
        additionalFloorCount: raw.type_of_work?.additional_floor_count,
        existingFloorCount: raw.type_of_work?.existing_floor_count,
      },
    },
    tasks,
  };
}

/**
 * Submits (or revises) the employee's work on a task: uploads the file, then records the
 * submission against the task (POST /api/employee/{employeeId}/task/{taskId}).
 */
export async function submitEmployeeTask(employeeId, taskId, data) {
  if (!employeeId || !taskId) throw new Error('Employee id and task id are required');

  const documentId = await ensureFileId(data.document);
  if (!documentId) throw new Error('Please upload a file');

  await api.post(`/api/employee/${employeeId}/task/${taskId}`, {
    revision_number: data.version,
    note: data.comment,
    document: documentId,
    document_name: data.document?.file?.name ?? null,
  });
}
